/* eslint-disable max-len */
/* eslint-disable react/prop-types */
/* eslint-disable object-curly-newline */
/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { Modal, Box } from '@mui/material';
import {
  Plus, RefreshCw, Users, Trash2,
} from 'lucide-react';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import { Navbar, Sidebar } from '../../components';
import DataTable from '../../components/DataTable';
import StatusPill from '../../components/StatusPill';
import ConfirmDialog from '../../components/ConfirmDialog';
import RowActionMenu from '../../components/RowActionMenu';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../utils/styledSelect';
import { PRIMARY_OWNER } from '../../constants/accountTypes';
import './roleManagement.css';

const dash = <span className="text-ink-500">—</span>;

const FIELD_LABEL = 'block text-sm font-medium text-ink-900 mb-1.5';
const FIELD_INPUT = 'w-full h-10 px-3 border border-rule rounded-[8px] text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-brand-500';
const BTN_GHOST = 'inline-flex items-center gap-1.5 px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition';
const BTN_PRIMARY = 'inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-sm font-semibold text-white bg-brand-700 hover:bg-[#1a3156] transition';
const BTN_GREEN = 'inline-flex items-center justify-center px-4 py-2 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition';

const MODAL_BOX = {
  width: 640,
  maxWidth: 'calc(100% - 32px)',
  maxHeight: '90vh',
  overflowY: 'auto',
  padding: 24,
  background: '#fff',
  borderRadius: 12,
};

// Toggleable permission chip (DS pill).
function PermChip({ label, active, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition disabled:opacity-50 disabled:cursor-not-allowed ${
        active ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-ink-700 border-rule hover:border-brand-500'
      }`}
      style={{ borderStyle: 'solid' }}
    >
      {label}
    </button>
  );
}

function RoleManagement() {
  const {
    activeWorkspace: workspace, accessToken, member: currentMember, refreshWorkspaceToken,
  } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPermissionDialog, setOpenPermissionDialog] = useState(false);
  const [openAssignRoleDialog, setOpenAssignRoleDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [members, setMembers] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Client-side sort (roles are fetched in full, no server paging).
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Form state
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [roleLevel, setRoleLevel] = useState(1);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const fetchRoles = useCallback(async () => {
    if (!workspace?.id || !accessToken) return;

    try {
      setLoading(true);
      const response = await sendRequest({
        url: API.ROLES.getRoles(workspace.id),
        accessToken,
      });

      if (response.data?.roles) {
        setRoles(response.data.roles);
      }
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to fetch roles',
      });
    } finally {
      setLoading(false);
    }
  }, [workspace?.id, accessToken, displayNotification]);

  const fetchPermissions = useCallback(async () => {
    if (!workspace?.id || !accessToken) return;

    try {
      const response = await sendRequest({
        url: API.ROLES.getPermissions(workspace.id),
        accessToken,
      });

      if (response.data?.permissions) {
        setPermissions(response.data.permissions);
      }
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to fetch permissions',
      });
    }
  }, [workspace?.id, accessToken, displayNotification]);

  const fetchMembers = useCallback(async () => {
    if (!workspace?.id || !accessToken) return;

    try {
      const response = await sendRequest({
        url: API.WORKSPACE.members(workspace.id),
        accessToken,
      });

      if (response.data?.members) {
        setMembers(response.data.members);
      }
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to fetch members',
      });
    }
  }, [workspace?.id, accessToken, displayNotification]);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    fetchMembers();
  }, [fetchRoles, fetchPermissions, fetchMembers]);

  const handleOpenDialog = (role = null) => {
    if (role) {
      setSelectedRole(role);
      setRoleName(role.name);
      setRoleDescription(role.description || '');
      setRoleLevel(role.level);
      setSelectedPermissions(role.permissions?.map((p) => p.id) || []);
    } else {
      setSelectedRole(null);
      setRoleName('');
      setRoleDescription('');
      setRoleLevel(1);
      setSelectedPermissions([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRole(null);
    setRoleName('');
    setRoleDescription('');
    setRoleLevel(1);
    setSelectedPermissions([]);
  };

  const togglePermission = (permId) => {
    setSelectedPermissions((prev) => (prev.includes(permId)
      ? prev.filter((id) => id !== permId)
      : [...prev, permId]));
  };

  const handleSaveRole = async () => {
    if (!workspace?.id || !accessToken || !roleName) return;

    try {
      setSaving(true);
      const payload = {
        name: roleName,
        description: roleDescription,
        level: roleLevel,
        permissionIds: selectedPermissions,
      };

      let needsRefresh = false;

      if (selectedRole) {
        // Update existing role
        await sendRequest({
          url: API.ROLES.updateRole(workspace.id, selectedRole.id),
          method: 'PUT',
          accessToken,
          data: payload,
        });

        // Check if current user has this role
        if (currentMember && currentMember.roleId === selectedRole.id) {
          needsRefresh = true;
        }

        displayNotification({
          severity: 'success',
          message: 'Role updated successfully',
        });
      } else {
        // Create new role
        await sendRequest({
          url: API.ROLES.createRole(workspace.id),
          method: 'POST',
          accessToken,
          data: payload,
        });
        displayNotification({
          severity: 'success',
          message: 'Role created successfully',
        });
      }

      handleCloseDialog();
      fetchRoles();

      // Refresh token if current user's role was modified
      if (needsRefresh) {
        await refreshWorkspaceToken();
      }
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to save role',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!workspace?.id || !accessToken || !deleteTarget) return;

    try {
      setDeleting(true);
      await sendRequest({
        url: API.ROLES.deleteRole(workspace.id, deleteTarget.id),
        method: 'DELETE',
        accessToken,
      });
      displayNotification({
        severity: 'success',
        message: 'Role deleted successfully',
      });
      setDeleteTarget(null);
      fetchRoles();
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to delete role',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenPermissionDialog = (role) => {
    setSelectedRole(role);
    setSelectedPermissions(role.permissions?.map((p) => p.id) || []);
    setOpenPermissionDialog(true);
  };

  const handleSavePermissions = async () => {
    if (!workspace?.id || !accessToken || !selectedRole) return;

    try {
      setSaving(true);
      await sendRequest({
        url: API.ROLES.assignPermissions(workspace.id, selectedRole.id),
        method: 'POST',
        accessToken,
        data: { permissionIds: selectedPermissions },
      });
      displayNotification({
        severity: 'success',
        message: 'Permissions updated successfully',
      });

      // If the current user has this role, refresh their token
      if (currentMember && currentMember.roleId === selectedRole.id) {
        await refreshWorkspaceToken();
      }

      setOpenPermissionDialog(false);
      fetchRoles();
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to update permissions',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAssignRole = async (memberId, roleId) => {
    if (!workspace?.id || !accessToken) return;

    try {
      await sendRequest({
        url: API.WORKSPACE.editMember(workspace.id, memberId),
        method: 'PUT',
        accessToken,
        data: { updateData: { roleId } },
      });
      displayNotification({
        severity: 'success',
        message: 'Role assigned successfully',
      });

      // If the current user's role was changed, refresh their token
      if (currentMember && currentMember.id === Number(memberId)) {
        await refreshWorkspaceToken();
      }

      fetchMembers();
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to assign role',
      });
    }
  };

  const groupPermissionsByResource = (perms) => {
    const grouped = {};
    perms.forEach((perm) => {
      if (!grouped[perm.resource]) {
        grouped[perm.resource] = [];
      }
      grouped[perm.resource].push(perm);
    });
    return grouped;
  };

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const groupedPermissions = groupPermissionsByResource(permissions);

  const sortedRoles = [...(Array.isArray(roles) ? roles : [])].sort((a, b) => {
    let av;
    let bv;
    if (sortKey === 'level') {
      av = a.level || 0;
      bv = b.level || 0;
    } else {
      av = (a.name || '').toLowerCase();
      bv = (b.name || '').toLowerCase();
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const roleActions = (role) => {
    const items = [];
    if (!role.isSystemRole) {
      items.push({ label: 'Edit Role', onClick: () => handleOpenDialog(role) });
    }
    items.push({
      label: role.isSystemRole ? 'View Permissions' : 'Manage Permissions',
      onClick: () => handleOpenPermissionDialog(role),
    });
    if (!role.isSystemRole) {
      items.push({ label: 'Delete Role', destructive: true, onClick: () => setDeleteTarget(role) });
    }
    return items;
  };

  const columns = [
    {
      key: 'name',
      label: 'Role Name',
      sortable: true,
      width: 200,
      flex: true,
      render: (r) => <span className="font-medium text-ink-900 truncate block" title={r.name}>{r.name}</span>,
    },
    {
      key: 'level',
      label: 'Level',
      sortable: true,
      width: 110,
      render: (r) => <StatusPill tone="info">{`Level ${r.level}`}</StatusPill>,
    },
    {
      key: 'description',
      label: 'Description',
      width: 280,
      flex: true,
      render: (r) => <span className="text-ink-700 truncate block" title={r.description}>{r.description || dash}</span>,
    },
    {
      key: 'permissions',
      label: 'Permissions',
      width: 140,
      render: (r) => <span className="text-ink-700">{`${r.permissions?.length || 0} permissions`}</span>,
    },
    {
      key: 'type',
      label: 'Type',
      width: 120,
      render: (r) => (
        <StatusPill tone={r.isSystemRole ? 'neutral' : 'info'}>
          {r.isSystemRole ? 'System' : 'Custom'}
        </StatusPill>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 72,
      align: 'center',
      render: (r) => <RowActionMenu items={roleActions(r)} ariaLabel={`Actions for ${r.name}`} />,
    },
  ];

  const assignableMembers = members.filter((m) => m.status && m.accountType !== PRIMARY_OWNER);

  return (
    <div>
      <Navbar />
      <Sidebar />
      <div className="screen-wrapper">
        <div className="screen-header" style={{ fontFamily: 'Inter, sans-serif' }}>
          {/* Header — title left, actions top-right */}
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between mb-5">
            <div className="min-w-0">
              <h1 className="font-bold text-ink-900 leading-tight truncate text-2xl lg:text-[32px] lg:leading-10" data-testid="title" data-tour="role-management">
                Role Management
              </h1>
              <p className="text-sm text-ink-500 mt-1" data-testid="subtitle">
                Define roles, manage permissions, and assign them to your team
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 flex-shrink-0 xl:justify-end">
              <button type="button" onClick={fetchRoles} disabled={loading} className={`${BTN_GHOST} disabled:opacity-50`} style={{ borderStyle: 'solid' }}>
                <RefreshCw size={16} />
                Refresh
              </button>
              <button type="button" onClick={() => setOpenAssignRoleDialog(true)} className={BTN_GHOST} style={{ borderStyle: 'solid' }}>
                <Users size={16} />
                Assign Roles
              </button>
              <button type="button" onClick={() => handleOpenDialog()} className={BTN_PRIMARY}>
                <Plus size={16} />
                Create Role
              </button>
            </div>
          </div>

          <DataTable
            columns={columns}
            rows={sortedRoles}
            getRowKey={(r) => r.id}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={toggleSort}
            loading={loading}
            emptyText="No roles found."
            minWidth={960}
          />
        </div>
      </div>

      {/* Create / Edit Role */}
      {openDialog && (
        <Modal open onClose={handleCloseDialog} className="flex-row align-c jstfy-c" data-testid="role-dialog">
          <Box style={MODAL_BOX}>
            <h5 className="text-lg font-semibold leading-none text-ink-900 mb-5">
              {selectedRole ? 'Edit Role' : 'Create New Role'}
            </h5>
            <div className="flex flex-col gap-4">
              <div>
                <span className={FIELD_LABEL}>Role Name</span>
                <input
                  type="text"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  placeholder="e.g. Regional Manager"
                  className={FIELD_INPUT}
                  style={{ borderStyle: 'solid' }}
                />
              </div>
              <div>
                <span className={FIELD_LABEL}>Description</span>
                <textarea
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                  placeholder="What this role is for"
                  className="w-full px-3 py-2 border border-rule rounded-[8px] text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-brand-500"
                  style={{ borderStyle: 'solid', minHeight: 72, resize: 'vertical' }}
                />
              </div>
              <div>
                <span className={FIELD_LABEL}>Level</span>
                <select
                  value={roleLevel}
                  onChange={(e) => setRoleLevel(parseInt(e.target.value, 10))}
                  className={`${STYLED_SELECT_CLASS} w-full`}
                  style={STYLED_SELECT_STYLE}
                >
                  {[1, 2, 3, 4, 5].map((l) => <option key={l} value={l}>{`Level ${l}`}</option>)}
                </select>
                <span className="block text-xs text-ink-500 mt-1.5">Higher level = more permissions</span>
              </div>
              <div>
                <span className={FIELD_LABEL}>Permissions</span>
                <div className="border border-rule rounded-[8px] p-3 overflow-auto" style={{ borderStyle: 'solid', maxHeight: 300 }}>
                  {Object.entries(groupedPermissions).map(([resource, perms]) => (
                    <div key={resource} className="mb-4 last:mb-0">
                      <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-2 capitalize" style={{ letterSpacing: '0.05em' }}>
                        {resource}
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {perms.map((perm) => (
                          <PermChip
                            key={perm.id}
                            label={perm.action}
                            active={selectedPermissions.includes(perm.id)}
                            onClick={() => togglePermission(perm.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-start items-center gap-2 mt-6">
              <button type="button" onClick={handleCloseDialog} className={BTN_GHOST} style={{ borderStyle: 'solid' }}>
                Cancel
              </button>
              <button type="button" onClick={handleSaveRole} disabled={!roleName || saving} className={BTN_GREEN}>
                {/* eslint-disable-next-line no-nested-ternary */}
                {saving ? 'Saving…' : selectedRole ? 'Update' : 'Create'}
              </button>
            </div>
          </Box>
        </Modal>
      )}

      {/* Manage Permissions */}
      {openPermissionDialog && (
        <Modal open onClose={() => setOpenPermissionDialog(false)} className="flex-row align-c jstfy-c" data-testid="permission-dialog">
          <Box style={MODAL_BOX}>
            <div className="flex items-center gap-2 mb-5 flex-wrap">
              <h5 className="text-lg font-semibold leading-none text-ink-900 mb-0">
                {`Permissions — ${selectedRole?.name || ''}`}
              </h5>
              {selectedRole?.isSystemRole && <StatusPill tone="warning">System Role</StatusPill>}
            </div>
            <div className="overflow-auto" style={{ maxHeight: 400 }}>
              {Object.entries(groupedPermissions).map(([resource, perms]) => (
                <div key={resource} className="mb-4 last:mb-0">
                  <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-2 capitalize" style={{ letterSpacing: '0.05em' }}>
                    {resource}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {perms.map((perm) => (
                      <PermChip
                        key={perm.id}
                        label={`${perm.action}${perm.description ? ` · ${perm.description}` : ''}`}
                        active={selectedPermissions.includes(perm.id)}
                        disabled={selectedRole?.isSystemRole}
                        onClick={() => {
                          if (selectedRole?.isSystemRole) return;
                          togglePermission(perm.id);
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {selectedRole?.isSystemRole && (
              <p className="text-xs text-ink-500 mt-3">System roles cannot be modified. You can view permissions only.</p>
            )}
            <div className="flex justify-start items-center gap-2 mt-6">
              <button type="button" onClick={() => setOpenPermissionDialog(false)} className={BTN_GHOST} style={{ borderStyle: 'solid' }}>
                {selectedRole?.isSystemRole ? 'Close' : 'Cancel'}
              </button>
              {!selectedRole?.isSystemRole && (
                <button type="button" onClick={handleSavePermissions} disabled={saving} className={BTN_GREEN}>
                  {saving ? 'Saving…' : 'Save Permissions'}
                </button>
              )}
            </div>
          </Box>
        </Modal>
      )}

      {/* Assign Roles to Users */}
      {openAssignRoleDialog && (
        <Modal open onClose={() => setOpenAssignRoleDialog(false)} className="flex-row align-c jstfy-c" data-testid="assign-role-dialog">
          <Box style={MODAL_BOX}>
            <h5 className="text-lg font-semibold leading-none text-ink-900 mb-5">Assign Roles to Users</h5>
            <div className="overflow-auto" style={{ maxHeight: 440 }}>
              {assignableMembers.length === 0 ? (
                <p className="text-sm text-ink-500">No assignable members found.</p>
              ) : assignableMembers.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between gap-4 p-3 border border-rule rounded-[8px] mb-2 last:mb-0"
                  style={{ borderStyle: 'solid' }}
                >
                  <div className="min-w-0">
                    <span className="block font-medium text-ink-900 truncate">
                      {`${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim() || dash}
                    </span>
                    {member.role && (
                      <span className="text-xs text-ink-500">{`Current: ${member.role.name}`}</span>
                    )}
                  </div>
                  <select
                    value={member.roleId || ''}
                    onChange={(e) => handleAssignRole(member.id, e.target.value)}
                    className={STYLED_SELECT_CLASS}
                    style={{ ...STYLED_SELECT_STYLE, minWidth: 200 }}
                  >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {`${role.name} (Level ${role.level})`}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="flex justify-start items-center gap-2 mt-6">
              <button type="button" onClick={() => setOpenAssignRoleDialog(false)} className={BTN_GHOST} style={{ borderStyle: 'solid' }}>
                Close
              </button>
            </div>
          </Box>
        </Modal>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <ConfirmDialog
          tone="danger"
          icon={<Trash2 size={24} />}
          title="Delete role?"
          description={`This will permanently delete the "${deleteTarget.name}" role. This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          confirming={deleting}
          onConfirm={handleDeleteRole}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

export default RoleManagement;
